import React, { useState } from "react";
import { Box, Typography, Dialog } from "@mui/material";
import SentimentDissatisfiedRoundedIcon from "@mui/icons-material/SentimentDissatisfiedRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";

import ViewContainer from "../../components/ViewContainer";
import { fonts, brand } from "../../themes/theme";
import { resolveByType } from "../../tenant/tenant";
import profilesData from "../../data/profiles.json";

const PROFILE_ICONS = {
    agotadaFracaso: SentimentDissatisfiedRoundedIcon,
    fatigaAcumulada: HourglassBottomRoundedIcon,
    paralizadaCoste: PaidRoundedIcon,
    shockInicial: BoltRoundedIcon,
    comunicacionMedica: ForumRoundedIcon,
};

// Estilo de la etiqueta segun el tipo de marcador (mismo vocabulario que el
// resto de la demo). Tinte suave + texto de color para leerse de un vistazo.
const KIND_STYLE = {
    autoinforme: { bg: "rgba(173,202,214,0.28)", fg: brand.navy },
    conductual: { bg: "rgba(50,72,202,0.10)", fg: brand.primary },
    kpi: { bg: brand.gray100, fg: brand.gray600 },
    catalogo: { bg: "rgba(255,95,36,0.12)", fg: brand.accent },
};

function scoreText(s) {
    return s.lessThan ? `<${s.scoreMax}%` : s.scoreMin === s.scoreMax ? `${s.scoreMax}%` : `${s.scoreMin}–${s.scoreMax}%`;
}

/**
 * HU-4 — Perfiles.
 * 5 bloques iguales (icono, nombre, fase, descripción, score, palabras clave),
 * en filas de 3 + 2 centradas — mismo tamaño en cualquier resolución.
 */
function ProfilesView() {
    const { t } = useTranslation();
    // Perfil abierto en el modal de "justificacion" (marcadores + fuentes).
    const [activeKey, setActiveKey] = useState(null);

    const data = resolveByType(profilesData);
    const segments = [...data.segments].sort((a, b) => b.scoreMax - a.scoreMax);
    const activeSeg = segments.find((s) => s.key === activeKey) || null;

    return (
        <ViewContainer title={t("views.profiles.title")} description={t("views.profiles.description")}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        flex: { xs: "0 0 auto", md: 1 },
                        minHeight: 0,
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        alignItems: { xs: "stretch", md: "center" },
                        alignContent: { xs: "flex-start", md: "center" },
                        gap: { xs: 1.5, sm: 2 },
                        overflowY: { xs: "visible", md: "hidden" },
                    }}
                >
                    {segments.map((s, i) => (
                        <ProfileCard
                            key={s.key}
                            segment={s}
                            rank={i}
                            t={t}
                            onOpen={() => setActiveKey(s.key)}
                        />
                    ))}
                </Box>

                <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.5, flexShrink: 0 }}>
                    {t("views.profiles.note")}
                </Typography>
            </Box>

            <ProfileDialog segment={activeSeg} t={t} onClose={() => setActiveKey(null)} />
        </ViewContainer>
    );
}

function ProfileCard({ segment: s, rank, t, onOpen }) {
    const Icon = PROFILE_ICONS[s.key] || SentimentDissatisfiedRoundedIcon;
    const color = brand.teal;
    const iconBg = "rgba(173,202,214,0.16)";
    const barTrack = "rgba(255,255,255,0.12)";

    const scoreLabel = scoreText(s);
    const scoreNote = t(`views.profiles.segments.${s.key}.scoreNote`, { defaultValue: "" });
    const avgCycle = t(`views.profiles.segments.${s.key}.avgDropoutCycle`, { defaultValue: "" });

    return (
        <Box
            onClick={onOpen}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen();
                }
            }}
            sx={{
                width: { xs: "100%", sm: "calc(50% - 8px)", md: "calc(33.333% - 11px)" },
                minHeight: { xs: 300, md: 0 },
                height: { xs: "auto", md: "calc(50% - 8px)" },
                minWidth: 0,
                position: "relative",
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "rgba(255,255,255,0.1)",
                bgcolor: brand.navy,
                background: `linear-gradient(165deg, #16344F 0%, ${brand.navy} 50%, ${brand.navyDark} 100%)`,
                boxShadow: "0 4px 16px rgba(10,36,62,0.2), 0 1px 3px rgba(10,36,62,0.12)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: { xs: 2, md: 2.25 },
                py: { xs: 2, md: 2.1 },
                overflow: "hidden",
                cursor: "pointer",
                outline: "none",
                transition: "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
                animation: `mentialFadeUp 0.45s ease ${rank * 70}ms both`,
                "&:hover, &:focus-visible": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 14px 32px rgba(10,36,62,0.28), 0 4px 10px rgba(10,36,62,0.16)",
                    borderColor: "rgba(173,202,214,0.35)",
                    "& .profile-desc": {
                        maxHeight: 140,
                        opacity: 1,
                        mt: 0.75,
                    },
                    "& .profile-hint": { opacity: 1 },
                },
            }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "13px",
                    bgcolor: iconBg,
                    color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ fontSize: 22 }} />
            </Box>

            <Typography
                sx={{
                    fontFamily: fonts.display,
                    fontSize: { xs: 16, md: 15.5 },
                    color: brand.white,
                    lineHeight: 1.22,
                    maxWidth: 230,
                    flexShrink: 0,
                }}
            >
                {t(`views.profiles.segments.${s.key}.name`)}
            </Typography>

            <Typography sx={{ fontSize: 11.5, color: brand.teal, mt: 0.5, lineHeight: 1.3, maxWidth: 230, flexShrink: 0 }}>
                {t(`views.profiles.segments.${s.key}.phase`)}
            </Typography>

            <Box
                className="profile-desc"
                sx={{
                    maxHeight: 0,
                    opacity: 0,
                    mt: 0,
                    width: "100%",
                    overflow: "hidden",
                    transition: "max-height 0.55s ease, opacity 0.5s ease 0.05s, margin 0.55s ease",
                }}
            >
                <Typography
                    sx={{
                        fontSize: 12,
                        color: "rgba(173,202,214,0.85)",
                        lineHeight: 1.4,
                        maxWidth: 250,
                        mx: "auto",
                    }}
                >
                    {t(`views.profiles.segments.${s.key}.description`)}
                </Typography>
            </Box>

            <Box sx={{ mt: 1.25, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                <Typography sx={{ fontFamily: fonts.display, fontSize: { xs: 27, md: 25 }, lineHeight: 1, color }}>{scoreLabel}</Typography>
                <Typography sx={{ fontSize: 10.5, color: "rgba(173,202,214,0.7)", mt: 0.25 }}>
                    {scoreNote || t("views.profiles.scoreLabel")}
                </Typography>
                <Box sx={{ width: "55%", maxWidth: 140, mt: 0.75, height: 4, borderRadius: 4, bgcolor: barTrack, overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${s.scoreMax}%`, borderRadius: 4, bgcolor: color }} />
                </Box>
                {avgCycle && (
                    <Typography sx={{ fontSize: 10.5, color: "rgba(173,202,214,0.85)", mt: 0.6 }}>
                        {t("views.profiles.avgCycleLabel")}: <Box component="span" sx={{ color: "#fff", fontWeight: 500 }}>{avgCycle}</Box>
                    </Typography>
                )}
            </Box>

            <Typography
                className="profile-hint"
                sx={{
                    mt: 1,
                    fontSize: 10.5,
                    fontWeight: 500,
                    color: brand.teal,
                    opacity: { xs: 0.85, md: 0.55 },
                    transition: "opacity 0.2s ease",
                    flexShrink: 0,
                }}
            >
                {t("views.profiles.detailHint")} →
            </Typography>
        </Box>
    );
}

// Modal de "Justificacion por perfil": tabla de marcadores que sustentan el
// perfil (tipo de marcador · marcador del catalogo · origen) + fuentes. El
// dato ya vive en profiles translation; aqui solo se presenta al hacer clic.
function ProfileDialog({ segment: s, t, onClose }) {
    const open = Boolean(s);
    const base = s ? `views.profiles.segments.${s.key}` : null;
    const markers = s ? t(`${base}.markers`, { returnObjects: true }) : [];
    const Icon = s ? PROFILE_ICONS[s.key] || SentimentDissatisfiedRoundedIcon : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden", m: { xs: 1.5, sm: 3 } } }}
        >
            {s && (
                <Box>
                    {/* Cabecera navy con icono, nombre, fase y score */}
                    <Box
                        sx={{
                            position: "relative",
                            p: { xs: 2.25, md: 2.75 },
                            background: `linear-gradient(165deg, #16344F 0%, ${brand.navy} 55%, ${brand.navyDark} 100%)`,
                            color: brand.white,
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            aria-label={t("views.profiles.closeLabel")}
                            onClick={onClose}
                            sx={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                border: 0,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(255,255,255,0.12)",
                                color: brand.white,
                                "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                            }}
                        >
                            <CloseRoundedIcon sx={{ fontSize: 18 }} />
                        </Box>

                        <Typography sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.09em", color: brand.teal, fontWeight: 600 }}>
                            {t("views.profiles.justificationTitle")}
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "11px",
                                    bgcolor: "rgba(173,202,214,0.16)",
                                    color: brand.teal,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                {Icon && <Icon sx={{ fontSize: 21 }} />}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontFamily: fonts.display, fontSize: { xs: 17, md: 19 }, lineHeight: 1.2, pr: 4 }}>
                                    {t(`${base}.name`)}
                                    <Box component="span" sx={{ color: brand.teal, ml: 1 }}>({scoreText(s)})</Box>
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: "rgba(173,202,214,0.85)", mt: 0.3 }}>
                                    {t(`${base}.phase`)}
                                </Typography>
                            </Box>
                        </Box>

                        <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.82)", lineHeight: 1.5, mt: 1.25 }}>
                            {t(`${base}.description`)}
                        </Typography>
                    </Box>

                    {/* Tabla de marcadores */}
                    <Box sx={{ p: { xs: 1.75, md: 2.25 } }}>
                        {/* Cabecera de columnas (solo desktop) */}
                        <Box
                            sx={{
                                display: { xs: "none", md: "grid" },
                                gridTemplateColumns: "150px 1fr 130px",
                                gap: 2,
                                px: 1,
                                pb: 0.75,
                                borderBottom: "1px solid",
                                borderColor: brand.gray100,
                            }}
                        >
                            <ColHead>{t("views.profiles.colKind")}</ColHead>
                            <ColHead>{t("views.profiles.colMarker")}</ColHead>
                            <ColHead>{t("views.profiles.colOrigin")}</ColHead>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            {(Array.isArray(markers) ? markers : []).map((m, i) => {
                                const ks = KIND_STYLE[m.kind] || KIND_STYLE.kpi;
                                return (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", md: "150px 1fr 130px" },
                                            gap: { xs: 0.5, md: 2 },
                                            alignItems: { md: "center" },
                                            px: 1,
                                            py: { xs: 1.1, md: 1 },
                                            borderBottom: "1px solid",
                                            borderColor: brand.gray100,
                                            "&:last-of-type": { borderBottom: "none" },
                                        }}
                                    >
                                        <Box>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "inline-block",
                                                    fontSize: 10.5,
                                                    fontWeight: 500,
                                                    lineHeight: 1.4,
                                                    color: ks.fg,
                                                    bgcolor: ks.bg,
                                                    borderRadius: "999px",
                                                    px: 1,
                                                    py: 0.3,
                                                }}
                                            >
                                                {t(`views.profiles.kindLabels.${m.kind}`)}
                                            </Box>
                                        </Box>
                                        <Typography sx={{ fontSize: 13, color: brand.navy, lineHeight: 1.4 }}>
                                            {m.text}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: brand.gray500,
                                                fontWeight: 500,
                                                textAlign: { xs: "left", md: "right" },
                                            }}
                                        >
                                            {m.origin}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}
        </Dialog>
    );
}

function ColHead({ children }) {
    return (
        <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: brand.gray500 }}>
            {children}
        </Typography>
    );
}

export default ProfilesView;
